<template>
  <div id="AdminUserTable">
    <div class="columns">
      <div class="column is-4-desktop is-12-mobile">
        <b-field>
          <b-input
            type="search"
            icon="magnify"
            custom-class="dtac-font"
            v-model="inputText"
            :placeholder="$t('users.desc_search_user')"
            @keyup.native.enter="confirmSearchUser"
          />
        </b-field>
      </div>
      <!-- Button Search -->
      <div class="column is-1-desktop is-12-mobile">
        <b-button
          type="is-primary"
          class="full-width dtac-font"
          @click.prevent="confirmSearchUser"
          >{{ $t("users.btn_search_user") }}</b-button
        >
      </div>
      <div class="column is-2 is-offset-5">
        <b-button
          outlined
          type="is-primary"
          icon-left="account-plus"
          class="full-width dtac-font"
          @click.prevent="popupFormCreateNewUser"
          >{{ $t("users.btn_create_user") }}</b-button
        >
      </div>
    </div>
    <!-- Admin User Table -->
    <b-table
      ref="admintable"
      detailed
      :striped="true"
      :data="adminUsers"
      :show-detail-icon="true"
      @details-open="rowDetailSelected"
      @details-close="selectedRow = null"
    >
      <template slot-scope="props">
        <b-table-column field="email" :label="$t('users.table_header.email')">
          <span v-if="props.row.email">{{ props.row.email }}</span>
          <span v-else>-</span>
        </b-table-column>

        <b-table-column field="name" :label="$t('users.table_header.name')">
          <span v-if="props.row.name">{{ props.row.name }}</span>
          <span v-else>-</span>
        </b-table-column>

        <b-table-column
          field="name_id"
          :label="$t('users.table_header.username')"
        >
          <span v-if="props.row.name_id">{{ props.row.name_id }}</span>
          <span v-else>-</span>
        </b-table-column>

        <b-table-column
          field="date"
          :label="$t('users.table_header.last_login')"
        >
          <span v-if="props.row.last_signin">{{
            props.row.last_signin | dateformatter
          }}</span>
          <span v-else>-</span>
        </b-table-column>

        <b-table-column
          field="status"
          :label="$t('users.table_header.channel')"
          centered
        >
          <span
            v-if="
              props.row.channel && props.row.channel.toLowerCase() !== 'unknown'
            "
          >
            {{ props.row.channel.toUpperCase() | trimString }}
          </span>
          <span v-else> - </span>
        </b-table-column>

        <b-table-column
          field="status"
          :label="$t('users.table_header.status')"
          centered
        >
          <span v-if="props.row.status === 'active'" class="tag is-success">{{
            $t("users.account_status.active")
          }}</span>
          <span
            v-else-if="props.row.status === 'pending'"
            class="tag is-warning"
            >{{ $t("users.account_status.pending") }}</span
          >
          <span v-else class="tag is-pink">{{
            $t("users.account_status.inactive")
          }}</span>
        </b-table-column>

        <b-table-column
          field="Action"
          :label="$t('users.table_header.action')"
          centered
        >
          <b-button
            outlined
            size="is-small"
            type="is-danger"
            icon-right="delete"
            @click="confirmRemoveAdminUser(props.row)"
          />
        </b-table-column>
      </template>

      <!-- Detail window -->
      <template slot="detail" slot-scope="props">
        <div class="container">
          <div class="columns is-multiline">
            <!-- Save Button -->
            <div class="column is-12" style="text-align: center;">
              <b-field v-if="props.row.channel">
                <b-input
                  v-model="props.row.channel"
                  custom-class="dtac-font"
                  :placeholder="
                    $t('users.form_create_user.channel_placeholder')
                  "
                  expanded
                ></b-input>
              </b-field>
            </div>
            <!-- Checkbox: Report-->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.report">{{
                  $t("users.table_details.report")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Map Area -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.map_area">{{
                  $t("users.table_details.map_area")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Manage User -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.user_manage">{{
                  $t("users.table_details.user_manage")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Register Customer -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.customer_register">{{
                  $t("users.table_details.customer_register")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Get Customers-->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.customer_list">{{
                  $t("users.table_details.customer_list")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Update Customers -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.customer_update">{{
                  $t("users.table_details.customer_update")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Update Distance-->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.customer_distance">{{
                  $t("users.table_details.customer_distance")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Get Prospect Customers -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.customer_propect">{{
                  $t("users.table_details.customer_propect")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Edit Prospect Customers -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox
                  v-model="props.row.roles.customer_prospect_update"
                  >{{
                    $t("users.table_details.customer_prospect_update")
                  }}</b-checkbox
                >
              </div>
            </div>
            <!-- Checkbox: Get Devices -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.device_list">{{
                  $t("users.table_details.device_list")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Update Devices -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.device_update">{{
                  $t("users.table_details.device_update")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Checkbox: Bypass OTP Devices -->
            <div class="column is-3">
              <div class="field">
                <b-checkbox v-model="props.row.roles.bypass_otp">{{
                  $t("users.table_details.bypass_otp")
                }}</b-checkbox>
              </div>
            </div>
            <!-- Save Button -->
            <div class="column is-12" style="text-align: center;">
              <b-button
                class="dtac-font"
                type="is-pink"
                @click="saveUserRoleOnRow(props.row)"
                >{{ $t("users.table_details.btn_save") }}</b-button
              >
            </div>
          </div>
        </div>
      </template>
    </b-table>
    <!-- Pagination -->
    <div style="padding: 1rem 0;">
      <b-pagination
        :total="totalItem"
        :current="currentPage"
        :per-page="itemPerPage"
        :icon-prev="'chevron-left'"
        :icon-next="'chevron-right'"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
        @change="paginationChange"
      />
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import AlertManager from "@/managers/AlertManager";
import UserApiManager from "@/apis/UserApiManager";
import NewAdminUserForm from "@/components/forms/NewAdminUserForm.vue";

export default {
  name: "AdminUserTable",
  data() {
    return {
      inputText: null,
      totalItem: null,
      itemPerPage: null,
      currentPage: 1,
      selectedRow: null,
      adminUsers: this.adminUserList,
      channels: ["Van", "Team"],
      selectedChannel: null
    };
  },
  filters: {
    trimString: function(value) {
      if (!value) {
        return "";
      } else {
        const displayLimit = 10;
        if (value.length > displayLimit) {
          return value.substr(0, displayLimit).concat("...");
        } else {
          return value;
        }
      }
    },
    dateformatter: function(value) {
      if (!value) return "";
      return dayjs(value).format("DD/MM/YYYY @ HH:mm");
    }
  },
  methods: {
    confirmSearchUser: function() {
      if (this.inputText && this.inputText !== "") {
        this.getSearchAdminUser(this.inputText);
      } else {
        this.getAdminUsersList();
      }
    },
    rowDetailDeselected: function() {
      this.$refs.admintable.toggleDetails(this.selectedRow);
      this.selectedRow = null;
    },
    rowDetailSelected: function(row) {
      if (this.selectedRow) {
        this.$refs.admintable.toggleDetails(this.selectedRow);
      }
      this.selectedRow = row;
    },
    paginationChange: function(selectedPage) {
      this.currentPage = selectedPage;
      this.getAdminUsersList();
    },
    popupFormCreateNewUser: function() {
      this.$buefy.modal.open({
        parent: this,
        component: NewAdminUserForm,
        hasModalCard: true,
        events: {
          completed_create_new_user: () => {
            this.getAdminUsersList();
          }
        }
      });
    },
    confirmRemoveAdminUser(adminProfile) {
      this.$buefy.dialog.confirm({
        title: "Remove Admin User",
        message:
          "Are you sure you want to permanently remove <b>" +
          adminProfile.name_id +
          "</b> profile?",
        confirmText: "Confirm",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.removeAdminUser(adminProfile._id)
      });
    },
    getSearchAdminUser: async function(searchString) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const adminUsersResponse = await UserApiManager.requestSearchUsers(
        searchString
      );
      loadingComponent.close();

      switch (adminUsersResponse.code) {
        case "0":
          this.adminUsers = adminUsersResponse.data.data;
          this.totalItem = adminUsersResponse.data.pagination.count;
          this.itemPerPage = adminUsersResponse.data.pagination.limit;
          break;

        default:
          break;
      }
    },
    getAdminUsersList: async function() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const adminUsersResponse = await UserApiManager.requestGetUserList(
        this.currentPage - 1
      );
      loadingComponent.close();

      switch (adminUsersResponse.code) {
        case "0":
          this.adminUsers = adminUsersResponse.data.data;
          this.totalItem = adminUsersResponse.data.pagination.count;
          this.itemPerPage = adminUsersResponse.data.pagination.limit;
          break;

        default:
          break;
      }
    },
    /*
      Request API to update an admin user's roles by their user id
      customer_prospect_update
    */
    saveUserRoleOnRow: async function(userProfile) {
      const roles = userProfile.roles;
      const newRoles = {
        report: roles.report ? roles.report : false,
        map_area: roles.map_area ? roles.map_area : false,
        device_list: roles.device_list ? roles.device_list : false,
        device_update: roles.device_update ? roles.device_update : false,
        user_manage: roles.user_manage ? roles.user_manage : false,
        customer_list: roles.customer_list ? roles.customer_list : false,
        customer_update: roles.customer_update ? roles.customer_update : false,
        customer_register: roles.customer_register
          ? roles.customer_register
          : false,
        customer_propect: roles.customer_propect
          ? roles.customer_propect
          : false,
        customer_prospect_update: roles.customer_prospect_update
          ? roles.customer_prospect_update
          : false,
        customer_distance: roles.customer_distance
          ? roles.customer_distance
          : false,
        bypass_otp: roles.bypass_otp ? roles.bypass_otp : false
      };
      const newChannel = userProfile.channel;
      const params = {
        roles: newRoles,
        channel: newChannel
      };

      this.requestUpdateAdminProfile(userProfile, params);
    },
    requestUpdateAdminProfile: async function(profile, params) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const updateRoleResult = await UserApiManager.requestUpdateUserById(
        profile._id,
        params
      );
      loadingComponent.close();

      switch (updateRoleResult.code) {
        case "0":
          AlertManager.showSuccessSnackbar("Update Role Successfully");
          this.rowDetailDeselected();
          break;
        default:
          AlertManager.showErrorSnackbar(updateRoleResult.message);
          this.restateUserProfile(profile);
          break;
      }
    },
    restateUserProfile: async function(row) {
      const userProfileResult = await UserApiManager.requestGetUserById(
        row._id
      );
      switch (userProfileResult.code) {
        case "0":
          // eslint-disable-next-line
          const profile = userProfileResult.data.user;
          row.roles = profile.roles;
          break;

        default:
          break;
      }
    },
    /*
      Request API to remove admin user by their user id
    */
    removeAdminUser: async function(userId) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const removeResult = await UserApiManager.requestRemoveUserById(userId);
      loadingComponent.close();

      switch (removeResult.code) {
        case "0":
          this.getAdminUsersList();
          AlertManager.showSuccessSnackbar("Remove Admin User Successfully");
          break;

        default:
          AlertManager.showErrorSnackbar(removeResult.message);
          break;
      }
    }
  },
  async mounted() {
    this.getAdminUsersList();
  }
};
</script>

<style></style>
